import { Analytics } from '@analytics';
import ResourceListItem from '@components/TapToTip/ResourceListItem';
import { StyleBreakpoints } from '@constants';
import { makeStyles } from '@material-ui/core/styles';
import QRCode from 'qrcode';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import CustomizableModal from '../../Common/CustomizableModal';
import AccessControlledButton from '../AccessControlledButton';
import messages from './messages';

const useStyles = makeStyles(theme => ({
  createButton: {
    width: '100%',

    [theme.breakpoints.up(StyleBreakpoints.xs)]: {
      width: 'fit-content'
    }
  },
  contentHeader: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    float: 'right',
    marginBottom: '16px',
    width: '100%',

    [theme.breakpoints.up(StyleBreakpoints.xs)]: {
      flexDirection: 'row',
      alignItems: 'center'
    }
  }
}));

const ListResources = ({ resources, deleteResource, openCreateResources, openEditResource, fetchResources  }) => {
  const intl = useIntl();
  const classes = useStyles();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);

  // Build a set of user IDs assigned to any resource's team
  const assignedUserIds = new Set();
  resources.forEach(r => {
    (r.teamUsers || []).forEach(u => {
      if (u && u._id) assignedUserIds.add(u._id.toString());
    });
  });

  const handleQRDownload = async (url, resourceName) => {
    const data = await QRCode.toDataURL(url).catch(err => {
      console.error(err);
    });

    let a = document.createElement('a');
    a.download = `Tip-Direct-${resourceName || 'Resource'}_QR.png`;
    a.href = data;
    a.click();
  };

  const openDeleteResourceModal = resource => {
    setIsDeleteModalOpen(true);
    setResourceToDelete(resource);
  };

  const handleModalClose = () => {
    setIsDeleteModalOpen(false);
    setResourceToDelete(null);
  };

  const handleResourceRemove = resourceId => {
    deleteResource(resourceId);
    Analytics.track('remove resource clicked');
    handleModalClose();
  };

  return (
    <>
      <div className="w-full flex flex-col">
        <div className={classes.contentHeader}>
          <div className="text-xl font-bold text-slate-800 tracking-tight mb-3">{intl.formatMessage(messages.title)}</div>

          <AccessControlledButton
            elementStyle={classes.createButton}
            feature={'ResourceTippingPoints'}
            title={intl.formatMessage(messages.createResource)}
            onClick={openCreateResources}
          />
        </div>
        <div className="flex flex-wrap gap-3 lg:gap-4">
          {resources
            .sort((a, b) => a.name.localeCompare(b.name))
            .map(resource => {
              return (
                <ResourceListItem
                  resource={resource}
                  key={resource._id}
                  deleteResource={openDeleteResourceModal}
                  editResource={openEditResource}
                  downloadQRCode={handleQRDownload}
                  fetchResources={fetchResources}
                  assignedUserIds={assignedUserIds} // <-- pass down
                />
              );
          })}
        </div>
      </div>
      {isDeleteModalOpen && (
        <CustomizableModal
          modern
          isOpen={isDeleteModalOpen}
          onClose={handleModalClose}
          handleHeaderCloseButton={handleModalClose}
          title={intl.formatMessage(messages.confirmAction)}
          body={
            <>
              <p className="text-slate-600">
                {intl.formatMessage(messages.removeResourceConfirm)}:{' '}
                <b className="font-semibold text-slate-800">{resourceToDelete.name}</b>
              </p>

              {resourceToDelete.assignedCardsAmount > 0 && (
                <p className="text-slate-500 text-sm mt-2">{intl.formatMessage(messages.deleteAssigned)}</p>
              )}
            </>
          }
          handleFooterCloseButton={handleModalClose}
          primaryActionButtonData={{
            content: intl.formatMessage(messages.confirm),
            onClick: () => handleResourceRemove(resourceToDelete._id),
            isDisabled: false
          }}
        />
      )}
    </>
  );
};

export default ListResources;
